import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Terminal } from "lucide-react";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import {
  useGt06Commands,
  useCreateGt06Command,
  useUpdateGt06Command,
  useDeleteGt06Command,
} from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import type { Gt06Command, Gt06CommandSmsFallbackRole } from "@/services/gt06-commands.service";
import { trackerProtocolLabel } from "@/lib/tracker-protocols";
import { useTrackerProtocols } from "@/hooks/queries/platform";

export const Route = createFileRoute("/admin/gt06-commands")({
  component: () => (
    <AdminAccessGate action="conta_integracoes">
      <Gt06CommandsPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Comandos GT06 — Administração — Wp Rastreamento" }] }),
});

function Gt06CommandsPage() {
  const { data: commands, isLoading } = useGt06Commands();

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Comandos GT06</h1>
          <p className="text-sm text-muted-foreground">
            Catálogo de comandos de texto bruto por protocolo (ex.: <code>RELAY,1#</code> no GT06 +
            senha do rastreador, quando o equipamento exigir) — cada comando só aparece no popup
            "Enviar comando" de veículos cadastrados com o mesmo protocolo, em Editar Veículo, aba
            Hardware e Simcard. Marque um comando como "Bloquear via SMS" e outro como "Desbloquear
            via SMS" (1 par por protocolo) para o sistema tentar automaticamente por SMS quando o
            Bloquear/Desbloquear normal falhar (veículo offline ou erro do rastreador).
          </p>
        </div>
        <Gt06CommandFormDialog
          trigger={
            <Button>
              <Plus className="h-4 w-4" />
              Cadastrar comando
            </Button>
          }
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !commands || commands.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Terminal className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum comando cadastrado ainda</p>
            <p className="text-xs text-muted-foreground">
              Clique em "Cadastrar comando" para adicionar o primeiro comando GT06.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {commands.map((command) => (
            <Gt06CommandCard key={command.id} command={command} />
          ))}
        </div>
      )}
    </div>
  );
}

function Gt06CommandCard({ command }: { command: Gt06Command }) {
  const deleteCommand = useDeleteGt06Command();
  const { data: trackerProtocols } = useTrackerProtocols();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteCommand.mutateAsync(command.id);
      toast.success("Comando excluído");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao excluir o comando");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="flex-1">
        <CardTitle className="text-base">{command.name}</CardTitle>
        <CardDescription className="font-mono text-xs">{command.commandText}</CardDescription>
        {command.password && (
          <p className="text-xs text-muted-foreground">
            Senha: <span className="font-mono">{command.password}</span>
          </p>
        )}
        {command.description && (
          <p className="line-clamp-3 text-xs text-muted-foreground">{command.description}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline" className="w-fit">
            {trackerProtocolLabel(trackerProtocols, command.protocol)}
          </Badge>
          {command.smsFallbackRole && (
            <Badge variant="secondary" className="w-fit">
              Fallback SMS: {command.smsFallbackRole === "block" ? "Bloquear" : "Desbloquear"}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex items-center justify-end gap-2 pt-0">
        <Gt06CommandFormDialog
          command={command}
          trigger={
            <Button variant="outline" size="sm">
              <Pencil className="h-3.5 w-3.5" />
              Editar
            </Button>
          }
        />
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            <Trash2 className="h-3.5 w-3.5" />
            Excluir
          </Button>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir "{command.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. O comando some do popup "Enviar comando" de todos
                os veículos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleteCommand.isPending}>
                {deleteCommand.isPending ? "Excluindo…" : "Excluir"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function Gt06CommandFormDialog({
  command,
  trigger,
}: {
  command?: Gt06Command;
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [commandText, setCommandText] = useState("");
  const [password, setPassword] = useState("");
  const [description, setDescription] = useState("");
  const [protocol, setProtocol] = useState("");
  const [smsFallbackRole, setSmsFallbackRole] = useState<Gt06CommandSmsFallbackRole | "none">("none");
  const createCommand = useCreateGt06Command();
  const updateCommand = useUpdateGt06Command();
  const { data: trackerProtocols } = useTrackerProtocols();
  const isEditing = Boolean(command);
  const isPending = createCommand.isPending || updateCommand.isPending;

  useEffect(() => {
    if (!open) return;
    setName(command?.name ?? "");
    setCommandText(command?.commandText ?? "");
    setPassword(command?.password ?? "");
    setDescription(command?.description ?? "");
    setProtocol(command?.protocol ?? "");
    setSmsFallbackRole(command?.smsFallbackRole ?? "none");
  }, [open, command]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome do comando");
      return;
    }
    if (!commandText.trim()) {
      toast.error("Informe o texto do comando");
      return;
    }
    if (!protocol) {
      toast.error("Informe o protocolo do comando");
      return;
    }
    try {
      const input = {
        name: name.trim(),
        commandText: commandText.trim(),
        password: password.trim() || null,
        description: description.trim() || null,
        protocol,
        smsFallbackRole: smsFallbackRole === "none" ? null : smsFallbackRole,
      };
      if (command) {
        await updateCommand.mutateAsync({ id: command.id, input });
        toast.success("Comando atualizado");
      } else {
        await createCommand.mutateAsync(input);
        toast.success("Comando criado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar o comando");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar comando" : "Cadastrar comando"}</DialogTitle>
          <DialogDescription>
            Texto exatamente como o protocolo GT06 espera (ex.: <code>RELAY,1#</code>). Se o
            equipamento exigir senha, ela é enviada junto automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="gt06-name">Nome/apelido</Label>
            <Input
              id="gt06-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Cortar combustível"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gt06-command">Texto do comando</Label>
            <Input
              id="gt06-command"
              value={commandText}
              onChange={(e) => setCommandText(e.target.value)}
              placeholder="RELAY,1#"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gt06-password">Senha do rastreador (opcional)</Label>
            <Input
              id="gt06-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ex: 123456"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gt06-description">Descrição/instrução de uso</Label>
            <Textarea
              id="gt06-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Para que serve, quando usar…"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gt06-protocol">Protocolo</Label>
            <Select value={protocol} onValueChange={setProtocol}>
              <SelectTrigger id="gt06-protocol">
                <SelectValue placeholder="Selecione o protocolo" />
              </SelectTrigger>
              <SelectContent>
                {trackerProtocols?.map((p) => (
                  <SelectItem key={p.id} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Só aparece no popup "Enviar comando" de veículos cadastrados com este protocolo.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gt06-sms-fallback">Papel no fallback SMS</Label>
            <Select value={smsFallbackRole} onValueChange={(v) => setSmsFallbackRole(v as typeof smsFallbackRole)}>
              <SelectTrigger id="gt06-sms-fallback">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                <SelectItem value="block">Usar para Bloquear via SMS</SelectItem>
                <SelectItem value="unblock">Usar para Desbloquear via SMS</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Só 1 comando pode ter cada papel — marcar aqui remove o papel de qualquer outro
              comando que já o tivesse.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
